// Query wiring for the portfolio dashboard (FT-70): the URL state (period +
// account), the params every panel is fetched with, and the shared query keys.
// Kept free of React and of `api.ts` so it is unit-testable and `api.ts` can use it.

import type { PortfolioQueryParams } from "@finance-os/shared-types/api";
import {
  DEFAULT_PORTFOLIO_PERIOD,
  PORTFOLIO_PERIODS,
  type PortfolioPanelConst,
  type PortfolioPeriodConst,
} from "@finance-os/shared-types/constants";

export interface PortfolioSelection {
  period: PortfolioPeriodConst;
  /** One broker account, or null for all of them. */
  accountId: string | null;
}

const PERIOD_PARAM = "period";
const ACCOUNT_PARAM = "account";

const isPeriod = (v: string | null): v is PortfolioPeriodConst =>
  v !== null && (PORTFOLIO_PERIODS as readonly string[]).includes(v);

/** The selection encoded in a page URL; anything missing or unknown falls back to the defaults. */
export function readSelection(search: URLSearchParams): PortfolioSelection {
  const period = search.get(PERIOD_PARAM);
  const account = search.get(ACCOUNT_PARAM);
  return {
    period: isPeriod(period) ? period : DEFAULT_PORTFOLIO_PERIOD,
    accountId: account === null || account === "" ? null : account,
  };
}

/** The URL search after changing the selection: defaults are omitted, unrelated params are kept. */
export function writeSelection(search: URLSearchParams, change: Partial<PortfolioSelection>): URLSearchParams {
  const merged: PortfolioSelection = { ...readSelection(search), ...change };
  const out = new URLSearchParams(search);
  if (merged.period === DEFAULT_PORTFOLIO_PERIOD) out.delete(PERIOD_PARAM);
  else out.set(PERIOD_PARAM, merged.period);
  if (merged.accountId === null) out.delete(ACCOUNT_PARAM);
  else out.set(ACCOUNT_PARAM, merged.accountId);
  return out;
}

/**
 * The query every panel is fetched with. `asOf` is chosen once per selection so
 * all five panels evaluate the same instant and re-render consistently.
 */
export function portfolioParams(selection: PortfolioSelection, asOf: string): PortfolioQueryParams {
  return {
    period: selection.period,
    as_of: asOf,
    ...(selection.accountId === null ? {} : { account_id: selection.accountId }),
  };
}

// Fixed order so the same params always yield the same URL.
const QUERY_KEYS = ["period", "account_id", "provider", "as_of"] as const satisfies readonly (keyof PortfolioQueryParams)[];

export function portfolioQueryString(params: PortfolioQueryParams): string {
  const q = new URLSearchParams();
  for (const key of QUERY_KEYS) {
    const value = params[key];
    if (value !== undefined) q.set(key, value);
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** React Query key of one panel: the panels of one selection differ only by name. */
export function portfolioQueryKey(panel: PortfolioPanelConst, params: PortfolioQueryParams): readonly unknown[] {
  return ["portfolio", panel, params];
}
